import type {
  ConditionalFormattingActions,
  ConditionalFormattingCondition,
  ConditionalFormattingFilterType,
  ConditionalFormattingRule
} from "./types";

export const DEFAULT_CONDITIONAL_FORMATTING_ACTIONS: ConditionalFormattingActions =
  {
    backgroundColor: "var(--ag-data-background-color, transparent)",
    textColor: "var(--ag-data-color, var(--ag-foreground-color))"
  };

type LegacyConditionalFormattingRule = {
  columnId?: string;
  filterType?: ConditionalFormattingFilterType;
  conditions?: ConditionalFormattingCondition[];
  actions?: ConditionalFormattingActions;
};

const isCurrentRule = (value: unknown): value is ConditionalFormattingRule => {
  if (!value || typeof value !== "object") return false;
  const rule = value as Partial<ConditionalFormattingRule>;
  return (
    typeof rule.columnId === "string" &&
    !!rule.defaultActions &&
    Array.isArray(rule.conditions) &&
    rule.conditions.every(
      (condition) =>
        !!condition &&
        typeof condition === "object" &&
        Array.isArray(condition.conditions) &&
        !!condition.actions
    )
  );
};

const cloneRule = (
  rule: ConditionalFormattingRule
): ConditionalFormattingRule => ({
  columnId: rule.columnId,
  defaultActions: { ...rule.defaultActions },
  conditions: rule.conditions.map((group) => ({
    ...group,
    conditions: group.conditions.map((condition) => ({ ...condition })),
    actions: { ...group.actions }
  }))
});

/**
 * Converts saved flat rules from the previous UI to one entry per column.
 * Current entries are also cloned so editor drafts never mutate persisted state.
 */
export const normalizeConditionalFormattingRules = (
  values: unknown
): ConditionalFormattingRule[] => {
  if (!Array.isArray(values)) return [];

  const byColumn = new Map<string, ConditionalFormattingRule>();
  values.forEach((value) => {
    if (isCurrentRule(value)) {
      const current = cloneRule(value);
      const existing = byColumn.get(current.columnId);
      if (existing) {
        existing.conditions.push(...current.conditions);
      } else {
        byColumn.set(current.columnId, current);
      }
      return;
    }

    const legacy = value as LegacyConditionalFormattingRule;
    if (!legacy?.columnId || !Array.isArray(legacy.conditions)) return;
    const existing =
      byColumn.get(legacy.columnId) ??
      ({
        columnId: legacy.columnId,
        defaultActions: { ...DEFAULT_CONDITIONAL_FORMATTING_ACTIONS },
        conditions: []
      } satisfies ConditionalFormattingRule);
    existing.conditions.push({
      filterType:
        legacy.filterType ?? legacy.conditions[0]?.filterType ?? "text",
      conditions: legacy.conditions.map((condition) => ({ ...condition })),
      actions: { ...(legacy.actions ?? {}) }
    });
    byColumn.set(legacy.columnId, existing);
  });

  return Array.from(byColumn.values());
};
